/**
 * Oqim obyekti va entalpiya.
 *
 * TENGLAMALAR
 *   H(oqim) = Σ_i n_i · M_i · ∫_{Tref}^{T} cp_i(T′) dT′      [J/s], sezgir entalpiya
 *   (reaksiya issiqligi alohida, reaktor tugunida ΔH_r orqali hisobga olinadi)
 *   T(H): H(T) − H* = 0 → bisection [150, 3000] K
 *
 * QABUL QILINGAN SODDALASHTIRISHLAR
 *   1. Ideal aralashma: aralashish issiqligi yo'q.
 *   2. Butun oqim bitta fazada (sxema `phase`), faza o'tish issiqligi hisobga olinmaydi.
 *      Separatorda kondensatsiya ΔH_vap ni e'tiborsiz qoldiradi — bu 4-bosqich
 *      chegarasi; 5–6-bosqichda faza ajratgich qo'shiladi.
 *   3. Bosim entalpiyaga ta'sir qilmaydi (ideal gaz / siqilmas suyuqlik).
 */
import { enthalpyChange, getSubstance } from '../properties';
import { bisection } from '../solvers';
import type { Phase, WarningSink } from '../types';

export interface Stream {
  id: string;
  /** komponent → mol/s */
  flow: Record<string, number>;
  T: number;
  P: number;
}

export function totalFlow(s: Stream): number {
  return Object.values(s.flow).reduce((a, b) => a + b, 0);
}

/** kg/s */
export function massFlow(s: Stream): number {
  let m = 0;
  for (const [id, n] of Object.entries(s.flow)) m += n * getSubstance(id).molarMass;
  return m;
}

export function moleFractions(s: Stream): Record<string, number> {
  const tot = totalFlow(s);
  const x: Record<string, number> = {};
  for (const [id, n] of Object.entries(s.flow)) x[id] = tot > 0 ? n / tot : 0;
  return x;
}

/** Sezgir entalpiya oqimi, J/s (Tref bazasida) */
export function enthalpy(s: Stream, phase: Phase, Tref: number, warnings?: WarningSink): number {
  let H = 0;
  for (const [id, n] of Object.entries(s.flow)) {
    if (n <= 0) continue;
    const sub = getSubstance(id);
    H += n * sub.molarMass * enthalpyChange(sub, phase, Tref, s.T, warnings);
  }
  return H;
}

/** Berilgan entalpiyaga mos harorat (sarf va tarkib o'zgarmas) */
export function temperatureFromEnthalpy(s: Stream, H: number, phase: Phase, Tref: number): number {
  if (totalFlow(s) <= 0) return s.T;
  const f = (T: number) => enthalpy({ ...s, T }, phase, Tref) - H;
  const r = bisection(f, 150, 3000, { xtol: 1e-6, ftol: 1e-6 });
  return r.value;
}

export function cloneStream(s: Stream): Stream {
  return { id: s.id, flow: { ...s.flow }, T: s.T, P: s.P };
}

export function emptyStream(id: string, components: readonly string[], T = 298.15, P = 101325): Stream {
  const flow: Record<string, number> = {};
  for (const c of components) flow[c] = 0;
  return { id, flow, T, P };
}
